import { defineMessages, type IntlShape } from 'react-intl';
import { getIntlKey } from '../../contexts';
import { useIntl } from '../../hooks';
import { type CareOrder, CareOrderStatusEnum } from '../../types';
import { isKeyIn, logger, type ValueOf } from '../../utils';
import { getCareOrderStatusColor } from '../../utils/careOrder';
import { Box } from '../Core';
import { Icon, IconName, type IconProps } from '../Icons';
import { IndicatorDotVariants, IndicatorDotWrapper } from '../IndicatorDot';
import { Tooltip } from '../Tooltip';
import { Wrapper } from './styles';
import { OrderStatusSize } from './types';

const messages = defineMessages({
  pendingNew: {
    defaultMessage: 'Pending New',
    id: 'Status.pendingNew',
  },
  new: {
    defaultMessage: 'New',
    id: 'Status.new',
  },
  working: {
    defaultMessage: 'Working',
    id: 'Status.working',
  },
  filled: {
    defaultMessage: 'Filled',
    id: 'Status.filled',
  },
  rejected: {
    defaultMessage: 'Rejected',
    id: 'Status.rejected',
  },
  filledAndCanceled: {
    defaultMessage: 'Filled & Canceled',
    id: 'Status.filledAndCanceled',
  },
  partiallyFilled: {
    defaultMessage: 'Partially Filled',
    id: 'Status.partiallyFilled',
  },
  canceled: {
    defaultMessage: 'Canceled',
    id: 'Status.canceled',
  },
  pendingCancel: {
    defaultMessage: 'Pending Cancel',
    id: 'Status.pendingCancel',
  },
  pendingReplace: {
    defaultMessage: 'Pending Replace',
    id: 'Status.pendingReplace',
  },
  filledAndPendingFix: {
    defaultMessage: 'Filled - Pending Fix',
    id: 'Status.filledAndPendingFix',
  },
  failedFill: {
    defaultMessage: 'Failed Fill',
    id: 'Status.failedFill',
  },
  unknown: {
    defaultMessage: 'Unknown',
    id: 'Status.unknown',
  },
});

const StatusIcon = ({
  status,
  pendingFix,
  ...props
}: { status: ValueOf<typeof CareOrderStatusEnum>; pendingFix?: boolean } & Omit<IconProps, 'icon' | 'color'>) => {
  const color = getCareOrderStatusColor(status, pendingFix);
  switch (status) {
    case CareOrderStatusEnum.PendingNew:
      return <Icon icon={IconName.CircleDotted} color={color} {...props} />;
    case CareOrderStatusEnum.New:
      return <Icon icon={IconName.CircleSolid} color={color} {...props} />;
    case CareOrderStatusEnum.Working:
      return <Icon icon={IconName.CircleDotted} color={color} {...props} />;
    case CareOrderStatusEnum.Rejected:
      return <Icon icon={IconName.ExclamationCircleSolid} color={color} {...props} />;
    case CareOrderStatusEnum.PartiallyFilled:
      return <Icon icon={IconName.Circle} color={color} {...props} />;
    case CareOrderStatusEnum.Canceled:
      return <Icon icon={IconName.CloseCircleSolid} color={color} {...props} />;
    case CareOrderStatusEnum.PendingCancel:
    case CareOrderStatusEnum.PendingReplace:
      return <Icon icon={IconName.Clock} color={color} {...props} />;
    case CareOrderStatusEnum.Filled:
    case CareOrderStatusEnum.FilledAndCanceled:
      return <Icon icon={IconName.CheckCircleSolid} color={color} {...props} />;
    case CareOrderStatusEnum.FailedFill:
      return <Icon icon={IconName.CloseCircle} color={color} {...props} />;
    case CareOrderStatusEnum.Unknown:
      return <Icon icon={IconName.ExclamationCircleSolid} color={color} {...props} />;
    default: {
      const _exhaustiveCheck: never = status;
      logger.error(new Error(`Unexpected OrdStatus: ${status}`));
      return null;
    }
  }
};

type CareOrderStatusProps = {
  status: ValueOf<typeof CareOrderStatusEnum>;
  pendingFix?: boolean;
  align?: 'right' | 'left';
  iconPlacement?: 'right' | 'left';
  text?: string;
  title?: string;
  size?: OrderStatusSize;
};

export function getCareOrderStatusText({
  status,
  pendingFix,
  formatMessage,
}: {
  status: CareOrder['status'];
  pendingFix?: boolean;
  formatMessage: IntlShape['formatMessage'];
}) {
  /** The shoe-horning of pendingFix in this file is bad and needs to be removed. The backend has to send me the details instead, and then CareOrder.ts can properly handle it. */
  const statusToUse = pendingFix ? ('filledAndPendingFix' satisfies keyof typeof messages) : status;

  const intlKey = getIntlKey(statusToUse);
  let orderStatusTextTranslated: string = statusToUse;
  if (isKeyIn(intlKey, messages)) {
    orderStatusTextTranslated = formatMessage(messages[intlKey]);
  }

  return orderStatusTextTranslated;
}

const StatusText = ({ status, pendingFix }: { status: ValueOf<typeof CareOrderStatusEnum>; pendingFix?: boolean }) => {
  const { formatMessage } = useIntl();
  return <>{getCareOrderStatusText({ status, pendingFix, formatMessage })}</>;
};

export function CareOrderStatus({
  status,
  pendingFix,
  align = 'right',
  text,
  size = OrderStatusSize.Default,
  ...props
}: CareOrderStatusProps) {
  let tooltipText = text;
  if (status === CareOrderStatusEnum.FailedFill) {
    tooltipText = 'The Care Order is terminal but is not properly Filled. It should be force canceled.';
  }

  const tooltipContent = !!tooltipText && <Box maxWidth="300px">{tooltipText}</Box>;

  return (
    <Box>
      {tooltipContent ? (
        <Tooltip tooltip={tooltipContent} usePortal>
          {renderOrderStatus()}
        </Tooltip>
      ) : (
        renderOrderStatus()
      )}
    </Box>
  );

  function renderOrderStatus() {
    return (
      <Wrapper iconPlacement="left" align={align} size={size} {...props}>
        <StatusText status={status} pendingFix={pendingFix} />
        <IndicatorDotWrapper show={!!tooltipText} variant={IndicatorDotVariants.Warning}>
          <StatusIcon
            status={status}
            pendingFix={pendingFix}
            size={size === OrderStatusSize.Default ? 'fontSizeLarge' : 'fontSizeSmall'}
          />
        </IndicatorDotWrapper>
      </Wrapper>
    );
  }
}
